import React, { useEffect, useRef, useState } from "react";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import { Chart, registerables } from "chart.js";
import { postApiCall } from "../../../utils/api";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import MaterialTable from "material-table";
import tableIcons from "../../component/MaterialIcon";
import { ThemeProvider, createTheme } from "@mui/material";
import QuizSummaryChart from "./component/quizsummary";
import Swal from "sweetalert2";

Chart.register(...registerables);

const StudentAnalytics = () => {
  const defaultMaterialTheme = createTheme();
  const [searchParams] = useSearchParams();

  const id = searchParams.get("id");
  const name = searchParams.get("name");
  const [selectedRow, setSelectedRow] = useState(null);
  // const location = useLocation();
  // const { id, name } = location.state || {};
  const [data, setData] = useState([]);
  const [analyticData, setAnalyticData] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await postApiCall("student-analytics-by-id", { id });
      if (res.success) {
        setAnalyticData(res.data);
        setData(res.data.moduleProgressMilestones);
        res.data.moduleProgressMilestones.forEach((data, index) => {
          data.serial = index + 1;
        });
      }
    } catch (error) {
      console.error("Error fetching student analytics:", error);
    }
  };

  if (!analyticData) return <div>Loading...</div>;

  const columns = [
    { title: "Sl. no", field: "serial", width: "10%" },

    {
      title: "Module",
      field: "module_name",
    },
    {
      title: "Level Completion",
      field: "module_name",
      render: (row) => (
        <div>
          {row.levels?.map((level, index) => (
            <div
              key={index}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginTop: 3,
              }}
            >
              <span>
                {level.name}: {level.completed ? "✅" : "❌"}
              </span>

              <button
                onClick={async () => {
                  // call your API here
                  console.log("Complete level:", id);
                  console.log("Complete level:", row.module_id);
                  console.log("Complete level:", level.name);
                  const contentTypeData =
                    level.name === "questions"
                      ? "questions"
                      : level.name === "worksheets"
                        ? "worksheets"
                        : level.name === "readings"
                          ? "readingmaterials"
                          : "video";
                  const res = await postApiCall(
                    "progress/auto-complete/all-progress",
                    {
                      user_id: id,
                      module_id: row.module_id,
                      contentType: contentTypeData,
                    },
                  );
                  if (res.success === true) {
                    Swal.fire({
                      title: "Successful",
                      text: res.message,
                      icon: "success",
                    });
                  } else {
                    Swal.fire({
                      title: "Error",
                      text: res.message,
                      icon: "error",
                    });
                  }
                }}
                // disabled={level.completed}
                className="btn btn-success"
                style={{
                  padding: "4px 8px",
                  fontSize: "12px",
                  // cursor: level.completed ? "not-allowed" : "pointer",
                }}
              >
                Complete
              </button>
            </div>
          ))}
        </div>
      ),
    },
  ];

  // Quiz Chart Data
  const quizLabels =
    analyticData.quizAnalytics?.map((q) => q.module_name) || [];
  const correctCounts =
    analyticData.quizAnalytics?.map((q) =>
      q.score.reduce((sum, s) => sum + s.correct, 0),
    ) || [];
  const wrongCounts =
    analyticData.quizAnalytics?.map((q) =>
      q.score.reduce((sum, s) => sum + s.wrong, 0),
    ) || [];

  const quizChartData = {
    labels: quizLabels,
    datasets: [
      {
        label: "Correct",
        data: correctCounts,
        backgroundColor: "rgba(75, 192, 192, 0.6)",
      },
      {
        label: "Wrong",
        data: wrongCounts,
        backgroundColor: "rgba(255, 99, 132, 0.6)",
      },
    ],
  };

  // Bloom Taxonomy (Doughnut)
  const bloomLabels = analyticData.bloomTaxonomyStats
    ? Object.keys(analyticData.bloomTaxonomyStats)
    : [];
  const bloomCorrect = bloomLabels.map(
    (level) => analyticData.bloomTaxonomyStats[level].correct || 0,
  );
  const bloomWrong = bloomLabels.map(
    (level) => analyticData.bloomTaxonomyStats[level].wrong || 0,
  );

  const bloomDoughnutData = {
    labels: bloomLabels,
    datasets: [
      {
        label: "Correct Answers",
        data: bloomCorrect,
        backgroundColor: ["#4caf50", "#2196f3", "#ff9800", "#e91e63"],
      },
      {
        label: "Wrong Answers",
        data: bloomWrong,
        backgroundColor: ["#f44336", "#9c27b0", "#3f51b5", "#00bcd4"],
      },
    ],
  };

  // Performance Line Chart
  const highTag = analyticData.performanceTags?.highPerformer?.tag;
  const lowTag = analyticData.performanceTags?.lowPerformer?.tag;
  const highCorrect = analyticData.performanceTags?.highPerformer?.correct || 0;
  const highWrong = analyticData.performanceTags?.highPerformer?.wrong || 0;
  const lowCorrect = analyticData.performanceTags?.lowPerformer?.correct || 0;
  const lowWrong = analyticData.performanceTags?.lowPerformer?.wrong || 0;

  const performanceLineData = {
    labels: ["Correct", "Wrong"],
    datasets: [],
  };

  if (highTag) {
    performanceLineData.datasets.push({
      label: `${highTag} (High)`,
      data: [highCorrect, highWrong],
      fill: false,
      borderColor: "green",
      tension: 0.4,
    });
  }

  if (lowTag) {
    performanceLineData.datasets.push({
      label: `${lowTag} (Low)`,
      data: [lowCorrect, lowWrong],
      fill: false,
      borderColor: "red",
      tension: 0.4,
    });
  }

  return (
    <div className="content-wrapper mt-4">
      <section className="content">
        <div className="container-fluid">
          <h2 className="mb-4 pt-4">
            📊 Student Analytics {name && `- ${name}`}
          </h2>

          {/* Row 1: Quiz & Bloom */}
          <div className="row">
            <div className="col-md-6 mb-4">
              <div className="card p-3">
                <h5>Quiz Performance Per Module</h5>
                {quizLabels.length > 0 ? (
                  <Bar data={quizChartData} options={{ responsive: true }} />
                ) : (
                  <p>No quiz data available.</p>
                )}
              </div>
            </div>
            <div className="col-md-6 mb-4">
              <div className="card p-3 position-relative">
                <h5>Quiz Ration: Right/Wrong Question</h5>
                {analyticData.quizSummary ? (
                  <QuizSummaryChart quizSummary={analyticData.quizSummary} />
                ) : (
                  <p>No interaction data available.</p>
                )}
              </div>
            </div>
          </div>

          {/* top 5 tags */}
          <div className="row">
            <div className="col-md-6 mb-4">
              <div className="card p-3">
                <h5>Bloom's Taxonomy Stats</h5>
                <div style={{ height: "400px", width: "100%" }}>
                  <Bar
                    data={{
                      labels: bloomLabels,
                      datasets: [
                        {
                          label: "Bloom's Taxonomy Levels",
                          data: bloomDoughnutData.datasets[0].data,
                          backgroundColor: [
                            "rgba(255, 99, 132, 0.7)",
                            "rgba(54, 162, 235, 0.7)",
                            "rgba(255, 206, 86, 0.7)",
                            "rgba(75, 192, 192, 0.7)",
                            "rgba(153, 102, 255, 0.7)",
                            "rgba(255, 159, 64, 0.7)",
                          ],
                          borderColor: [
                            "rgba(255, 99, 132, 1)",
                            "rgba(54, 162, 235, 1)",
                            "rgba(255, 206, 86, 1)",
                            "rgba(75, 192, 192, 1)",
                            "rgba(153, 102, 255, 1)",
                            "rgba(255, 159, 64, 1)",
                          ],
                          borderWidth: 1,
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      indexAxis: "y",
                      scales: {
                        x: {
                          stacked: true,
                          title: {
                            display: true,
                            text: "Percentage (%)",
                          },
                          ticks: {
                            callback: function (value) {
                              return value + "%";
                            },
                          },
                          max: 100,
                        },
                        y: {
                          stacked: true,
                          title: {
                            display: true,
                            text: "Bloom's Taxonomy Levels",
                          },
                        },
                      },
                      plugins: {
                        legend: {
                          position: "bottom",
                        },
                        tooltip: {
                          callbacks: {
                            label: function (context) {
                              const total = context.dataset.data.reduce(
                                (a, b) => a + b,
                                0,
                              );
                              const value = context.parsed.x;
                              const percentage = Math.round(
                                (value / total) * 100,
                              );
                              return `${percentage}% (${value} questions)`;
                            },
                          },
                        },
                      },
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="col-md-6 mb-4">
              <div className="card p-3">
                <h5>Top 5 best Performance Tags</h5>
                {analyticData.top5PerformingTags?.length === 0 ? (
                  <p>NA.</p>
                ) : (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 40 }}>
                    <div>
                      <h6>✅</h6>
                      {analyticData.top5PerformingTags.map((item, index) => (
                        <p key={index}>{item.tag}</p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Row 2: Interactions & Performance Tags */}
          <div className="row">
            <div className="col-md-6 mb-4">
              <div className="card p-3">
                <h5>Tags Needing Attention</h5>
                {analyticData.needsToLearnTags?.rightTags?.length === 0 &&
                analyticData.needsToLearnTags?.wrongTags?.length === 0 ? (
                  <p>No tag analytics available.</p>
                ) : (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 40 }}>
                    {analyticData.needsToLearnTags?.rightTags?.length > 0 && (
                      <div>
                        <h6>✅ Right Tags</h6>
                        <ul>
                          {analyticData.needsToLearnTags.rightTags.map(
                            (tag, i) => (
                              <li key={i}>{tag}</li>
                            ),
                          )}
                        </ul>
                      </div>
                    )}
                    {analyticData.needsToLearnTags?.wrongTags?.length > 0 && (
                      <div>
                        <h6>❌ Wrong Tags</h6>
                        <ul>
                          {analyticData.needsToLearnTags.wrongTags.map(
                            (tag, i) => (
                              <li key={i}>{tag}</li>
                            ),
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-12 mt-4">
              <div className="card card-primary">
                <ThemeProvider theme={defaultMaterialTheme}>
                  <MaterialTable
                    icons={tableIcons}
                    title="Module Progress"
                    columns={columns}
                    data={data}
                    onRowClick={(evt, selectedRow) =>
                      setSelectedRow(selectedRow.tableData.id)
                    }
                    options={{
                      exportButton: true,
                      actionsColumnIndex: -1,
                      headerStyle: {
                        backgroundColor: "rgb(0 123 255)",
                        color: "#FFF",
                        fontSize: 18,
                      },
                      rowStyle: (rowData, index) => ({
                        backgroundColor:
                          selectedRow === rowData.tableData.id
                            ? "#d3d4d1"
                            : index % 2 !== 0
                              ? "#EEE"
                              : "#FFF",
                      }),
                    }}
                  />
                </ThemeProvider>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default StudentAnalytics;
